import { useTranslation } from 'react-i18next';

import { Stack } from '@mui/material';

import Button from '../../../../../components/Button';
import Note from '../../../../../components/Note';
import { ToBeDone } from '../../../../../db/types';
import { i18nNS } from '../../../../../i18n';
import AlertCircleIcon from '../../../../../icons/AlertCircleIcon';
import CheckmarkIcon from '../../../../../icons/CheckmarkIcon';
import RocketIcon from '../../../../../icons/RocketIcon';
import { format } from '../../../../../utils/datetime';
import DurationCell from './DurationCell';
import Skeleton, { EditPublishPreferencesDrawerFooterSkeleton } from './Skeleton';
import StudentResponses from './StudentResponses';
import {
  Cell,
  CellLabel,
  CellValue,
  Drawer,
  SaveAsDefaultCheckbox,
  SectionTitle,
  StyledSwitch,
  SwitchHelperText,
} from './styles';
import useEditPublishPreferencesDrawer, { Props as VMProps } from './vm';

interface Props extends VMProps {}

const EditPublishPreferencesDrawer = ({ ...vmOptions }: Props) => {
  const { t } = useTranslation([i18nNS.POLL, i18nNS.GLOSSARY, i18nNS.COMMON]);

  const { open, isPublishing } = vmOptions;
  const {
    cls,
    poll,
    onCloseDrawer,
    formMethods,
    onSubmit,
    onUpdateStudentResponses: handleUpdateStudentResponses,
    onUpdateDuration: handleUpdateDuration,
  } = useEditPublishPreferencesDrawer(vmOptions);

  const { handleSubmit, control, watch } = formMethods;

  const [allowLate, deadlineFirst, saveAsDefault] = watch(['allowLate', 'deadlineFirst', 'saveAsDefault']);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onCloseDrawer}
      header={t(isPublishing ? 'publishing_poll' : 'poll_preferences', { ns: i18nNS.POLL })}
      footer={
        !cls || !poll ? (
          <EditPublishPreferencesDrawerFooterSkeleton />
        ) : (
          <>
            <SaveAsDefaultCheckbox
              control={control}
              name="saveAsDefault"
              label={t('save_as_default', { ns: i18nNS.POLL })}
              aria-label={t(
                saveAsDefault
                  ? 'uncheck_to_not_set_form_values_as_default'
                  : 'check_to_set_form_values_as_default',
                { ns: i18nNS.POLL }
              )}
            />
            <Button
              variant="text"
              size="small"
              onClick={onCloseDrawer}
              aria-label={t('click_to_close_drawer', { ns: i18nNS.COMMON })}
            >
              {t('cancel', { ns: i18nNS.GLOSSARY })}
            </Button>
            <Button
              size="small"
              startIcon={
                isPublishing ? (
                  <RocketIcon size={16} variant="filled" aria-hidden />
                ) : (
                  <CheckmarkIcon size={16} aria-hidden />
                )
              }
              onClick={handleSubmit(onSubmit)}
              aria-label={t(
                isPublishing ? 'click_to_publish_poll' : 'click_to_save_poll_publish_preferences',
                {
                  ns: i18nNS.POLL,
                }
              )}
            >
              {t(isPublishing ? 'publish' : 'save', { ns: i18nNS.GLOSSARY })}
            </Button>
          </>
        )
      }
    >
      {!cls || !poll ? (
        <Skeleton />
      ) : (
        <Stack gap={8}>
          <Stack gap={6}>
            <Stack gap={2}>
              <Cell>
                <CellLabel>{t('class', { ns: i18nNS.GLOSSARY })}</CellLabel>
                <CellValue>{format(cls.startTime, 'hh:mm a, MMM dd, yyyy')}</CellValue>
              </Cell>
              <Cell>
                <CellLabel>{t('to_be_done', { ns: i18nNS.POLL })}</CellLabel>
                <CellValue>
                  {t(poll.toBeDone === ToBeDone.PRE_CLASS ? 'pre_class' : 'in_class', { ns: i18nNS.POLL })}
                </CellValue>
              </Cell>
            </Stack>
            <StudentResponses
              control={control}
              name="studentResponses"
              onUpdateStudentResponses={handleUpdateStudentResponses}
            />
            <DurationCell control={control} name="duration" onUpdateDuration={handleUpdateDuration} />
          </Stack>

          <Stack gap={3}>
            <SectionTitle>{t('options', { ns: i18nNS.GLOSSARY })}</SectionTitle>
            <div>
              <StyledSwitch
                control={control}
                name="allowLate"
                label={t('allow_late_submissions', { ns: i18nNS.POLL })}
                aria-label={t(
                  allowLate ? 'turn_off_to_not_allow_late_submissions' : 'turn_on_to_allow_late_submissions',
                  { ns: i18nNS.POLL }
                )}
              />
              <SwitchHelperText>
                {allowLate
                  ? t(
                      'late_submissions_will_be_marked_as_late_allowing_for_late_submissions_can_increase_participation',
                      { ns: i18nNS.POLL }
                    )
                  : t('students_wont_be_able_to_respond_to_the_poll_after_the_deadline', { ns: i18nNS.POLL })}
              </SwitchHelperText>
            </div>
            <div>
              <StyledSwitch
                control={control}
                name="deadlineFirst"
                label={t('withhold_results_till_deadline', { ns: i18nNS.POLL })}
                aria-label={t(
                  deadlineFirst
                    ? 'turn_off_to_declare_results_as_soon_as_submitting_activity'
                    : 'turn_on_to_withhold_results_till_deadline',
                  { ns: i18nNS.POLL, activity: t('poll', { ns: i18nNS.GLOSSARY }) }
                )}
              />
              <SwitchHelperText>
                {t(
                  deadlineFirst
                    ? 'students_will_see_the_poll_results_not_upon_submitting_a_response_but_after_the_poll_deadline_is_over'
                    : 'students_will_see_their_scores_and_answers_as_soon_as_they_submit_their_responses',
                  { ns: i18nNS.POLL }
                )}
              </SwitchHelperText>
            </div>

            {isPublishing && (
              <Note color="warning" icon={<AlertCircleIcon aria-hidden />}>
                {t('the_course_participants_will_be_notified_instantly_via_email_and_push_notifications', {
                  ns: i18nNS.POLL,
                })}
                <br />
                {t('once_published_the_poll_cannot_be_deleted', {
                  ns: i18nNS.POLL,
                })}
              </Note>
            )}
          </Stack>
        </Stack>
      )}
    </Drawer>
  );
};

export default EditPublishPreferencesDrawer;
